import React, { useState, useEffect, useCallback } from "react";
import { AdminApis } from "../../apis/adminApi";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { useParams } from "react-router-dom";
import configs from "../../configs";
import {
  FaWhatsapp, FaCreditCard, FaStore, FaSearch, FaShoppingBag,
  FaInstagram, FaFacebook, FaTiktok, FaEye, FaShieldAlt, FaTruck,
  FaHeadset, FaPercent, FaChevronDown, FaThLarge, FaList, FaTimes,
  FaLock, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaArrowRight,
  FaBoxOpen, FaStar,
} from "react-icons/fa";
import { PhoneInput } from "react-contact-number-input";
import { Oval } from "react-loader-spinner";
import { PaymentApis } from "../../apis/paymentApis";

export default function CardViewProductPage() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [data, setData] = useState([]);
  const [marketInfo, setMarketInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("default");
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [paymentForm, setPaymentForm] = useState({ fullName: "", email: "", phoneNumber: "", quantity: 1 });

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (paymentModal) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [paymentModal]);

  useEffect(() => {
    if (searchParams.get("status") === "cancelled") navigate(`/s/${params?.storeId}`);
    if (searchParams.get("tx_ref")) {
      PaymentApis.getProdutCallback(searchParams.get("tx_ref")).then((res) => {
        if (res?.data?.success === true) {
          if (res?.data?.data?.status === "successful") { navigate(`/s/${params?.storeId}`); toast.success("Payment successful!"); }
          else toast.error("Payment failed");
        }
      }).catch(() => { });
    }
  }, [searchParams, params, navigate]);

  useEffect(() => { fetchStoreData(); }, []);

  useEffect(() => {
    let filtered = data?.products?.data || [];
    if (activeCategory !== "all") filtered = filtered.filter((p) => p.category === activeCategory);
    if (searchTerm.trim()) filtered = filtered.filter((p) =>
      p.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.product_description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (sortBy === "price-low") filtered = [...filtered].sort((a, b) => parseFloat(a.product_price) - parseFloat(b.product_price));
    else if (sortBy === "price-high") filtered = [...filtered].sort((a, b) => parseFloat(b.product_price) - parseFloat(a.product_price));
    else if (sortBy === "name") filtered = [...filtered].sort((a, b) => a.product_name.localeCompare(b.product_name));
    setFilteredProducts(filtered);
  }, [searchTerm, data, sortBy, activeCategory]);

  const fetchStoreData = async (page = "") => {
    try {
      setLoading(true);
      const res = await AdminApis.getProductByLinkName(params?.storeId, page);
      if (res?.data) { setData(res.data.data); setMarketInfo(res.data.data.market_info); }
    } catch { } finally { setLoading(false); }
  };

  const handlePageChange = useCallback((page) => fetchStoreData(page), []);

  const handlePayment = async (e) => {
    e.preventDefault();
    const paymentData = {
      user_id: selectedProduct?.user_id,
      amount: selectedProduct?.product_price * paymentForm.quantity,
      customer_full_name: paymentForm.fullName,
      product_qty: paymentForm.quantity,
      pay_for: selectedProduct?.product_name,
      store_id: params?.storeId,
      customer_email: paymentForm.email,
      customer_phone_number: paymentForm.phoneNumber?.countryCode + paymentForm.phoneNumber?.phoneNumber,
    };
    try {
      const res = await PaymentApis.payForProduct(paymentData);
      if (res?.data?.success) { window.location.replace(res.data.data.data.link); setPaymentModal(false); }
    } catch { toast.error("Payment failed. Please try again."); }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(price || 0);

  const primary = marketInfo?.brand_primary_color || "#2563EB";
  const storeName = params?.storeId?.replace(/-/g, " ");
  const allProducts = data?.products?.data || [];
  const uniqueCategories = ["all", ...new Set(allProducts.map((p) => p.category).filter(Boolean))];
  const totalProducts = data?.products?.total || allProducts.length;

  /* ── Loading ─────────────────────────────────────────────────────── */
  if (loading && !data?.products) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: `linear-gradient(135deg, #0f0c29 0%, #302b63 50%, ${primary} 100%)` }}>
        <div className="text-center text-white">
          <Oval height={52} width={52} color="#fff" secondaryColor="rgba(255,255,255,0.3)" strokeWidth={3} />
          <p className="mt-5 text-lg font-bold">Loading Store…</p>
          <p className="text-sm text-white/60 mt-1">Setting up your shopping experience</p>
        </div>
      </div>
    );
  }

  /* ── Render ──────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-[#f4f6f9]">

      {/* ── Announcement Bar ──────────────────────────────────────────── */}
      <div
        className="py-2.5 text-center text-white text-xs font-semibold tracking-widest uppercase relative overflow-hidden"
        style={{ backgroundColor: primary }}
      >
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.15) 10px, rgba(255,255,255,0.15) 20px)" }} />
        <span className="relative"><FaTruck className="inline mr-2" size={11} /> Delivery across Nigeria — shop with confidence</span>
      </div>

      {/* ── Sticky Header ─────────────────────────────────────────────── */}
      <header className={`transition-all duration-500 z-40 ${isScrolled ? "sticky top-0 bg-white/90 backdrop-blur-xl shadow-lg border-b border-gray-100" : "relative bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between py-3 md:py-4 gap-3">

            <div className="flex items-center gap-3 flex-shrink-0">
              {marketInfo?.brand_logo && marketInfo.brand_logo !== "no image" ? (
                <img src={marketInfo.brand_logo} alt="logo" className="w-10 h-10 md:w-11 md:h-11 object-contain rounded-xl border border-gray-200 shadow-sm bg-white p-0.5" />
              ) : (
                <div className="w-10 h-10 md:w-11 md:h-11 rounded-xl flex items-center justify-center text-white shadow-md" style={{ backgroundColor: primary }}>
                  <FaStore size={17} />
                </div>
              )}
              <div>
                <h1 className="text-sm md:text-base font-extrabold text-gray-900 capitalize leading-tight">{storeName}</h1>
                <p className="text-[11px] text-gray-400 font-medium">Official Store</p>
              </div>
            </div>

            {/* Desktop search (shows when scrolled) */}
            {isScrolled && (
              <div className="hidden md:flex flex-1 max-w-sm mx-6 relative">
                <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                <input
                  type="text"
                  placeholder="Search products…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-100 border border-gray-200 rounded-full text-sm focus:outline-none focus:bg-white focus:ring-2 transition-all"
                  style={{ "--tw-ring-color": primary + "40" }}
                />
              </div>
            )}

            {marketInfo?.phone_number && (
              <a
                href={`${configs?.baseRedirect}/${marketInfo.phone_number}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 text-white text-xs md:text-sm font-bold rounded-full shadow-md hover:opacity-90 active:scale-95 transition-all"
                style={{ backgroundColor: "#25D366" }}
              >
                <FaWhatsapp size={15} />
                <span className="hidden sm:inline">Chat with Us</span>
              </a>
            )}
          </div>
        </div>
      </header>

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" style={{ minHeight: "52vh", background: `linear-gradient(140deg, #0f0c29 0%, #1a1560 40%, ${primary} 100%)` }}>
        {/* Mesh / texture */}
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }} />

        {/* Gradient orbs */}
        <div className="absolute top-[-80px] left-[-80px] w-[380px] h-[380px] rounded-full blur-[120px] opacity-25" style={{ backgroundColor: primary }} />
        <div className="absolute bottom-[-60px] right-[-60px] w-[300px] h-[300px] rounded-full blur-[100px] opacity-20 bg-purple-500" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] rounded-full blur-[80px] opacity-10 bg-white" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-10 md:py-14 text-center text-white flex flex-col items-center">

          {/* Logo */}
          {marketInfo?.brand_logo && marketInfo.brand_logo !== "no image" ? (
            <div className="relative mb-6">
              <div className="absolute inset-0 rounded-2xl blur-xl opacity-50" style={{ backgroundColor: primary }} />
              <img src={marketInfo.brand_logo} alt="logo" className="relative w-16 h-16 md:w-20 md:h-20 object-contain rounded-2xl bg-white/15 backdrop-blur-md p-2.5 border border-white/20 shadow-2xl" />
            </div>
          ) : (
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center mb-5 shadow-2xl">
              <FaStore size={36} className="text-white/80" />
            </div>
          )}

          {/* Store name */}
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black capitalize tracking-tight mb-3 leading-[1.1]" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.3)" }}>
            {storeName}
          </h2>

          {/* Description */}
          <p className="text-base md:text-lg text-white/70 max-w-lg mx-auto mb-3 leading-relaxed font-medium">
            {marketInfo?.brand_description || "Discover amazing products at unbeatable prices. Quality guaranteed."}
          </p>

          {/* Stats */}
          {totalProducts > 0 && (
            <div className="flex items-center gap-2 mb-8">
              <div className="flex -space-x-1">
                {[...Array(Math.min(3, totalProducts))].map((_, i) => (
                  <div key={i} className="w-6 h-6 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center">
                    <FaBoxOpen size={9} className="text-white" />
                  </div>
                ))}
              </div>
              <span className="text-sm text-white/80 font-semibold">{totalProducts} {totalProducts === 1 ? "product" : "products"} available</span>
            </div>
          )}

          {/* CTAs */}
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="#products"
              className="group flex items-center gap-2 px-8 py-3.5 bg-white text-gray-900 rounded-full font-bold text-sm hover:bg-gray-100 transition-all duration-300 shadow-2xl hover:shadow-white/20 hover:-translate-y-0.5"
            >
              Shop Now
              <FaArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
            </a>
            {marketInfo?.phone_number && (
              <a
                href={`${configs?.baseRedirect}/${marketInfo.phone_number}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-8 py-3.5 rounded-full font-bold text-sm border border-white/30 bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-300 hover:-translate-y-0.5"
              >
                <FaWhatsapp size={15} /> Chat with Us
              </a>
            )}
          </div>

          {/* Social links */}
          {(marketInfo?.facebook_url || marketInfo?.instagram_url || marketInfo?.tiktok_url) && (
            <div className="flex items-center gap-3 mt-8">
              {[
                { url: marketInfo?.facebook_url, Icon: FaFacebook },
                { url: marketInfo?.instagram_url, Icon: FaInstagram },
                { url: marketInfo?.tiktok_url, Icon: FaTiktok },
              ].filter((s) => s.url).map(({ url, Icon }, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/25 transition-all">
                  <Icon size={14} className="text-white/80" />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-12 md:h-16">
            <path d="M0 60L480 20L960 50L1440 10V60H0Z" fill="#f4f6f9" />
          </svg>
        </div>
      </section>

      {/* ── Trust Badges ──────────────────────────────────────────────── */}
      <section className="bg-white border-y border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 md:py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { Icon: FaTruck, label: "Fast Delivery", sub: "Across Nigeria", bg: "bg-blue-50", color: "text-blue-600", border: "border-blue-100" },
              { Icon: FaShieldAlt, label: "Secure Payment", sub: "100% protected", bg: "bg-emerald-50", color: "text-emerald-600", border: "border-emerald-100" },
              { Icon: FaHeadset, label: "24/7 Support", sub: "Always here", bg: "bg-purple-50", color: "text-purple-600", border: "border-purple-100" },
              { Icon: FaPercent, label: "Best Prices", sub: "Guaranteed value", bg: "bg-orange-50", color: "text-orange-600", border: "border-orange-100" },
            ].map(({ Icon, label, sub, bg, color, border }) => (
              <div key={label} className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-2xl ${bg} border ${border} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={color} size={17} />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900 leading-tight">{label}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Products Section ──────────────────────────────────────────── */}
      <section id="products" className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 pb-16">

        {/* Search */}
        <div className="mb-5">
          <div className="relative max-w-lg">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input
              type="text"
              placeholder="Search products…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-10 py-3.5 bg-white border border-gray-200 rounded-2xl text-sm shadow-sm focus:outline-none focus:ring-2 focus:bg-white transition-all"
              style={{ focusRingColor: primary }}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                <FaTimes size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Category Pills */}
        {uniqueCategories.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-3 mb-5 -mx-1 px-1" style={{ scrollbarWidth: "none" }}>
            {uniqueCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold capitalize transition-all duration-200 border ${activeCategory === cat ? "text-white border-transparent shadow-lg scale-105" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:shadow-sm"
                  }`}
                style={activeCategory === cat ? { backgroundColor: primary, borderColor: primary } : {}}
              >
                {cat === "all" ? "All Products" : cat}
              </button>
            ))}
          </div>
        )}

        {/* Controls bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
          <div>
            <h3 className="text-xl font-extrabold text-gray-900">
              {activeCategory === "all" ? "All Products" : <span className="capitalize">{activeCategory}</span>}
            </h3>
            <p className="text-sm text-gray-400 mt-0.5">
              {filteredProducts.length} {filteredProducts.length === 1 ? "product" : "products"}
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none bg-white border border-gray-200 rounded-xl px-4 py-2.5 pr-9 text-sm font-semibold text-gray-700 focus:outline-none cursor-pointer shadow-sm hover:border-gray-300 transition-colors"
              >
                <option value="default">Sort: Default</option>
                <option value="price-low">Price: Low → High</option>
                <option value="price-high">Price: High → Low</option>
                <option value="name">Name: A → Z</option>
              </select>
              <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={11} />
            </div>
            <div className="hidden md:flex items-center bg-gray-100 rounded-xl p-1 gap-0.5">
              {[{ mode: "grid", Icon: FaThLarge }, { mode: "list", Icon: FaList }].map(({ mode, Icon }) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`p-2.5 rounded-lg transition-all ${viewMode === mode ? "bg-white shadow text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
                >
                  <Icon size={13} />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Product Grid */}
        {filteredProducts.length > 0 ? (
          <div className={`grid gap-4 md:gap-5 ${viewMode === "grid" ? "grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" : "grid-cols-1"
            }`}>
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className={`group bg-white rounded-3xl overflow-hidden border border-gray-100 hover:border-gray-200 hover:shadow-2xl hover:-translate-y-1 transition-all duration-400 ${viewMode === "list" ? "flex" : "flex flex-col"
                  }`}
              >
                {/* Image */}
                <div className={`relative overflow-hidden bg-gray-50 flex-shrink-0 ${viewMode === "list" ? "w-40 sm:w-48 h-full min-h-[140px]" : "aspect-[4/3]"}`}>
                  {product.product_image_1 ? (
                    <img
                      src={product.product_image_1}
                      alt={product.product_name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                      <FaShoppingBag className="text-gray-300" size={viewMode === "list" ? 28 : 36} />
                    </div>
                  )}

                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />

                  {/* Sale badge */}
                  {product.no_of_items && parseFloat(product.no_of_items) < parseFloat(product.product_price) && (
                    <div className="absolute top-3 left-3 px-2.5 py-1 bg-red-500 text-white text-[10px] font-black rounded-full shadow-md uppercase tracking-wide">
                      Sale
                    </div>
                  )}

                  {/* Category tag */}
                  {product.category && viewMode === "grid" && (
                    <div className="absolute top-3 right-3 px-2 py-0.5 bg-black/40 backdrop-blur-sm text-white text-[9px] font-semibold rounded-full capitalize opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {product.category}
                    </div>
                  )}

                  {/* Quick view */}
                  <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-300">
                    <NavLink
                      to={`/storedetails/${product.id}`}
                      className="flex items-center justify-center gap-1.5 py-2 bg-white/95 backdrop-blur-sm text-gray-900 text-xs font-bold rounded-xl shadow-lg hover:bg-white transition-colors"
                    >
                      <FaEye size={11} /> Quick View
                    </NavLink>
                  </div>
                </div>

                {/* Info */}
                <div className={`flex flex-col flex-1 p-4 ${viewMode === "list" ? "justify-between" : ""}`}>
                  <div>
                    {viewMode === "list" && product.category && (
                      <span className="inline-block text-[10px] font-bold capitalize px-2 py-0.5 rounded-full mb-2 text-white" style={{ backgroundColor: primary }}>
                        {product.category}
                      </span>
                    )}
                    <NavLink to={`/storedetails/${product.id}`}>
                      <h4 className="font-bold text-gray-900 text-sm leading-snug mb-2 line-clamp-2 hover:opacity-80 transition-opacity capitalize">
                        {product.product_name}
                      </h4>
                    </NavLink>
                    {viewMode === "list" && product.product_description && (
                      <p className="text-gray-400 text-xs mb-3 line-clamp-2 leading-relaxed">{product.product_description}</p>
                    )}
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="text-base font-black" style={{ color: primary }}>
                        {formatPrice(product.product_price)}
                      </span>
                      {product.no_of_items && parseFloat(product.no_of_items) < parseFloat(product.product_price) && (
                        <span className="text-xs text-gray-300 line-through font-medium">
                          {formatPrice(product.no_of_items)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* CTA buttons */}
                  <div className={`flex gap-2 ${viewMode === "list" ? "" : "flex-col"}`}>
                    <button
                      onClick={() => { setSelectedProduct(product); setPaymentForm((p) => ({ ...p, quantity: 1 })); setPaymentModal(true); }}
                      className="flex-1 py-2.5 text-white text-xs font-bold rounded-xl hover:opacity-90 active:scale-95 transition-all shadow-md hover:shadow-lg"
                      style={{ backgroundColor: primary }}
                    >
                      Buy Now
                    </button>
                    <a
                      href={`${configs?.baseRedirect}/${product.phone_number}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-green-700 bg-green-50 text-xs font-bold rounded-xl hover:bg-green-100 active:scale-95 transition-all border border-green-100"
                    >
                      <FaWhatsapp size={13} /> Chat
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mb-6 shadow-inner">
              <FaShoppingBag className="text-gray-300" size={36} />
            </div>
            <h3 className="text-xl font-extrabold text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-400 text-sm mb-6 max-w-xs">
              {searchTerm || activeCategory !== "all" ? "Try adjusting your search or category filter." : "Check back soon for new arrivals!"}
            </p>
            {(searchTerm || activeCategory !== "all") && (
              <button
                onClick={() => { setSearchTerm(""); setActiveCategory("all"); }}
                className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-bold rounded-full hover:bg-gray-50 transition-colors shadow-sm"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}

        {/* Pagination */}
        {data?.products?.links && data.products.links.length > 3 && (
          <div className="flex justify-center mt-12">
            <div className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-2xl p-1.5 shadow-sm">
              {data.products.links.map((link, i) => {
                const isNav = link.label === "Next &raquo;" || link.label === "&laquo; Previous";
                const label = link.label === "&laquo; Previous" ? "←" : link.label === "Next &raquo;" ? "→" : link.label;
                return (
                  <button
                    key={i}
                    onClick={() => { if (isNav) handlePageChange(link.url?.charAt(link.url.length - 1)); else if (!isNaN(link.label)) handlePageChange(link.label); }}
                    disabled={link.active || !link.url}
                    className={`min-w-[36px] h-9 px-3 text-sm font-bold rounded-xl transition-all ${link.active ? "text-white shadow-md" : link.url ? "text-gray-600 hover:bg-gray-100" : "text-gray-300 cursor-not-allowed"}`}
                    style={link.active ? { backgroundColor: primary } : {}}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <footer style={{ background: "linear-gradient(135deg, #0f0c29 0%, #1a1560 60%, #1e1a6e 100%)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-10">

            {/* Brand */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-5">
                {marketInfo?.brand_logo && marketInfo.brand_logo !== "no image" ? (
                  <img src={marketInfo.brand_logo} alt="logo" className="w-12 h-12 object-contain rounded-xl bg-white/10 p-1.5 border border-white/10" />
                ) : (
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: primary }}>
                    <FaStore size={22} className="text-white" />
                  </div>
                )}
                <div>
                  <h4 className="text-xl font-extrabold capitalize text-white">{storeName}</h4>
                  <p className="text-white/40 text-xs">Official Store</p>
                </div>
              </div>
              <p className="text-white/50 text-sm leading-relaxed mb-6 max-w-sm">
                {marketInfo?.brand_description || "Your trusted destination for quality products. We deliver excellence with every order."}
              </p>
              <div className="flex gap-2.5">
                {[
                  { url: marketInfo?.facebook_url, Icon: FaFacebook },
                  { url: marketInfo?.instagram_url, Icon: FaInstagram },
                  { url: marketInfo?.tiktok_url, Icon: FaTiktok },
                  { url: marketInfo?.phone_number ? `${configs?.baseRedirect}/${marketInfo.phone_number}` : null, Icon: FaWhatsapp },
                ].filter((s) => s.url).map(({ url, Icon }, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-white/10 border border-white/10 flex items-center justify-center hover:bg-white/20 hover:border-white/20 transition-all">
                    <Icon size={15} className="text-white/70" />
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h5 className="font-bold text-xs uppercase tracking-widest text-white/30 mb-4">Shop</h5>
              <ul className="space-y-3 text-sm text-white/50">
                <li><a href="#products" className="hover:text-white transition-colors">All Products</a></li>
                {uniqueCategories.filter(c => c !== "all").slice(0, 4).map(cat => (
                  <li key={cat}><button onClick={() => { setActiveCategory(cat); document.getElementById("products")?.scrollIntoView({ behavior: "smooth" }); }} className="capitalize hover:text-white transition-colors">{cat}</button></li>
                ))}
              </ul>
            </div>

            <div>
              <h5 className="font-bold text-xs uppercase tracking-widest text-white/30 mb-4">Contact</h5>
              <ul className="space-y-3 text-sm text-white/50">
                {marketInfo?.phone_number && (
                  <li className="flex items-center gap-2.5"><FaPhoneAlt size={11} className="flex-shrink-0 text-white/30" />{marketInfo.phone_number}</li>
                )}
                {marketInfo?.email && (
                  <li className="flex items-center gap-2.5"><FaEnvelope size={11} className="flex-shrink-0 text-white/30" />{marketInfo.email}</li>
                )}
                {marketInfo?.address && (
                  <li className="flex items-start gap-2.5"><FaMapMarkerAlt size={11} className="flex-shrink-0 mt-0.5 text-white/30" />{marketInfo.address}</li>
                )}
                {marketInfo?.phone_number && (
                  <li>
                    <a href={`${configs?.baseRedirect}/${marketInfo.phone_number}`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold text-white transition-all hover:opacity-90"
                      style={{ backgroundColor: "#25D366" }}>
                      <FaWhatsapp size={12} /> Chat Now
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/30 text-xs text-center">
              © {new Date().getFullYear()} <span className="capitalize">{storeName}</span>. All rights reserved.
            </p>
            <a href="https://www.mygupta.co" target="_blank" rel="noopener noreferrer"
              className="text-white/30 hover:text-white/60 text-xs transition-colors">
              Powered by <span className="font-bold text-white/50">Gupta</span>
            </a>
          </div>
        </div>
      </footer>

      {/* ── Floating WhatsApp ─────────────────────────────────────────── */}
      {marketInfo?.phone_number && (
        <a
          href={`${configs?.baseRedirect}/${marketInfo.phone_number}`}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-5 w-14 h-14 flex items-center justify-center rounded-full shadow-2xl hover:scale-110 active:scale-95 transition-transform z-40"
          style={{ backgroundColor: "#25D366" }}
        >
          <FaWhatsapp size={26} className="text-white" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white animate-pulse" />
        </a>
      )}

      {/* ── Payment Modal (native — bottom sheet on mobile, centered on desktop) ── */}
      {paymentModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setPaymentModal(false)}
          />

          {/* Sheet */}
          <div className="relative w-full sm:max-w-md bg-white sm:rounded-3xl rounded-t-3xl overflow-hidden shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[85vh]">

            {/* Drag handle (mobile) */}
            <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>

            {/* Modal header */}
            <div className="px-5 py-4 flex-shrink-0 relative" style={{ background: `linear-gradient(135deg, ${primary}, ${primary}cc)` }}>
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-lg font-extrabold text-white">Checkout</h2>
                  <p className="text-white/70 text-xs mt-0.5">Complete your purchase securely</p>
                </div>
                <button
                  onClick={() => setPaymentModal(false)}
                  className="p-2 text-white/60 hover:text-white hover:bg-white/20 rounded-full transition-colors"
                >
                  <FaTimes size={14} />
                </button>
              </div>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Product summary */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl border border-gray-100">
                {selectedProduct?.product_image_1 ? (
                  <img src={selectedProduct.product_image_1} alt={selectedProduct.product_name} className="w-16 h-16 object-cover rounded-xl flex-shrink-0 shadow-sm" />
                ) : (
                  <div className="w-16 h-16 bg-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FaShoppingBag className="text-gray-300" size={22} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 text-sm line-clamp-2 leading-snug">{selectedProduct?.product_name}</p>
                  <p className="text-lg font-extrabold mt-1" style={{ color: primary }}>
                    {formatPrice(selectedProduct?.product_price)}
                  </p>
                </div>
              </div>

              {/* Form fields */}
              <div className="space-y-3.5">
                {[
                  { label: "Full Name", type: "text", key: "fullName", placeholder: "Your full name" },
                  { label: "Email Address", type: "email", key: "email", placeholder: "you@email.com" },
                ].map(({ label, type, key, placeholder }) => (
                  <div key={key}>
                    <label className="block text-xs font-bold text-gray-600 mb-1.5">{label}</label>
                    <input
                      type={type}
                      required
                      value={paymentForm[key]}
                      onChange={(e) => setPaymentForm((p) => ({ ...p, [key]: e.target.value }))}
                      placeholder={placeholder}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:bg-white transition-all"
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Phone Number</label>
                  <PhoneInput required countryCode="ng" onChange={(phone) => setPaymentForm((p) => ({ ...p, phoneNumber: phone }))} placeholder="Phone number" />
                </div>

                {/* Quantity */}
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5">Quantity</label>
                  <div className="flex items-center gap-4">
                    <button type="button" onClick={() => setPaymentForm((p) => ({ ...p, quantity: Math.max(1, p.quantity - 1) }))}
                      className="w-11 h-11 rounded-xl border-2 border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 flex items-center justify-center text-xl font-bold transition-all active:scale-95">
                      −
                    </button>
                    <span className="w-12 text-center font-extrabold text-xl text-gray-900">{paymentForm.quantity}</span>
                    <button type="button" onClick={() => setPaymentForm((p) => ({ ...p, quantity: p.quantity + 1 }))}
                      className="w-11 h-11 rounded-xl border-2 border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 flex items-center justify-center text-xl font-bold transition-all active:scale-95">
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Order summary */}
              <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Unit price</span>
                  <span className="font-semibold">{formatPrice(selectedProduct?.product_price || 0)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Quantity</span>
                  <span className="font-semibold">× {paymentForm.quantity}</span>
                </div>
                <div className="border-t border-gray-200 pt-2 flex justify-between">
                  <span className="font-extrabold text-gray-900">Total</span>
                  <span className="text-xl font-extrabold" style={{ color: primary }}>
                    {formatPrice((selectedProduct?.product_price || 0) * paymentForm.quantity)}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-center gap-1.5 text-gray-400 text-xs">
                <FaLock size={9} /> Secured by Flutterwave
              </div>
            </div>

            {/* Sticky pay button */}
            <form onSubmit={handlePayment} className="flex-shrink-0 p-4 border-t border-gray-100 bg-white">
              <button
                type="submit"
                className="w-full py-4 text-white font-extrabold text-sm rounded-2xl shadow-lg hover:shadow-xl active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                style={{ backgroundColor: primary }}
              >
                <FaLock size={12} />
                Pay {formatPrice((selectedProduct?.product_price || 0) * paymentForm.quantity)}
              </button>
            </form>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={3000} theme="light" toastStyle={{ borderRadius: "12px" }} />
    </div>
  );
}
